import { Button } from '@app/components/various/Button';
import { StickyModalFooter } from '@app/components/various/Modal';
import { getIsFolderNavigationItem } from '@app/logic/lookbook';
import { useLookbookDetails } from '@app/services/hooks/lookbook/useLookbookDetails';
import { useUpdateLookbookPages } from '@app/services/hooks/lookbook/useUpdateLookbookPages';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useLookbookListingContext } from '../context/LookbookListingContext';

import { AddedNavigationItems } from './AddedNavigationItems/AddedNavigationItems';
import { AvailableNavigationItems } from './AvailableNavigationItems/AvailableNavigationItems';
import { useLookbookNavigationSettingsContext } from './LookbookNavigationSettingsContext';

export const LookbookSettingsModalContent = () => {
  const { t } = useTranslation(['common', 'settings', 'lookbook']);
  const { selectedLookbookId, setIsSettingsModalVisible } = useLookbookListingContext();
  const { formMethods, addedMenuItems } = useLookbookNavigationSettingsContext();
  const { mutate, isPending } = useUpdateLookbookPages();
  const { isLoading, data } = useLookbookDetails({ lookbookId: selectedLookbookId });

  const navigationItems = React.useMemo(() => {
    return data?.navigationItems ?? [];
  }, [data?.navigationItems]);

  const navigationItemsWithIds = React.useMemo(
    () =>
      navigationItems.map(parentItem => {
        const { children, id: parentId } = parentItem;

        const childrenWithParentId = children?.map(child => ({
          ...child,
          parentId,
        }));

        const childrenToReturn = isDefined(childrenWithParentId) ? { children: childrenWithParentId } : {};

        if (getIsFolderNavigationItem(parentItem)) {
          return {
            ...parentItem,
            ...childrenToReturn,
            id: parentId,
          };
        }

        return {
          ...parentItem,
          ...childrenToReturn,
          id: parentId,
        };
      }),
    [navigationItems],
  );

  function handleSave(): void {
    if (!isDefined(selectedLookbookId)) {
      return;
    }

    mutate(
      {
        data: {
          items: addedMenuItems,
        },
        lookbookId: selectedLookbookId,
      },
      { onSuccess: () => setIsSettingsModalVisible(false) },
    );
  }

  React.useEffect(() => {
    formMethods.reset(
      { ...formMethods.getValues(), navigationItems: navigationItemsWithIds },
      {
        keepDefaultValues: formMethods.formState.isDirty,
        keepDirty: formMethods.formState.isDirty,
      },
    );
  }, [formMethods, navigationItemsWithIds]);

  return (
    <>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <AvailableNavigationItems isLoading={isLoading} />
        <AddedNavigationItems isLoading={isLoading} />
      </div>
      <StickyModalFooter>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" color="secondary" onClick={() => setIsSettingsModalVisible(false)}>
            {t('common:cancel')}
          </Button>
          <Button disabled={isPending || isLoading} onClick={handleSave}>
            {t('common:save')}
          </Button>
        </div>{' '}
      </StickyModalFooter>
    </>
  );
};
